import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Download, Play, Server, Sparkles, Trash2, Upload } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import {
  AGENT_SKILL_MAX_BYTES,
  type AgentInstallJob,
  type AgentSetup,
  type UpdateAgentSetupBody,
} from '@tasks/contracts';

import {
  applyAgent,
  cancelAgentInstall,
  deleteAgent,
  deleteAgentSkill,
  getAgentInstall,
  getAgentSetup,
  listAgentSkills,
  startSharedAgentInstall,
  testAgentLlm,
  testAgentServer,
  uninstallAgent,
  updateAgent,
  updateAgentSetup,
  uploadAgentSkill,
} from '../../api/agents';
import { queryKeys } from '../../api/queryKeys';
import { Badge, type BadgeTone } from '../../components/Badge';
import { Button } from '../../components/Button';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { EmptyState } from '../../components/EmptyState';
import { Input } from '../../components/Input';
import { Page, PageHeader, PageSection } from '../../components/Page';
import { Skeleton } from '../../components/Skeleton';
import { useToast } from '../../components/Toast';
import { useAuth } from '../../auth/AuthProvider';
import { humanizeError } from '../../lib/errors';
import styles from './AgentsPages.module.css';

const STATUS_LABEL: Record<string, string> = {
  not_installed: 'Не установлен',
  queued: 'В очереди',
  installing: 'Установка…',
  installed: 'Установлен',
  failed: 'Ошибка',
  uninstalling: 'Удаление…',
};

const STATUS_TONE: Record<string, BadgeTone> = {
  not_installed: 'neutral',
  queued: 'warning',
  installing: 'progress',
  installed: 'success',
  failed: 'danger',
  uninstalling: 'neutral',
};

interface FormState {
  serverHost: string;
  serverPort: string;
  serverPassword: string;
  llmBaseUrl: string;
  llmModel: string;
  llmApiKey: string;
  trigger: string;
  work: string;
  review: string;
  approved: string;
  maxParallelJobs: string;
  jobTimeoutMinutes: string;
  maxDiffLines: string;
  maxFiles: string;
  branchPrefix: string;
}

function formOf(setup: AgentSetup): FormState {
  return {
    serverHost: setup.server.host,
    serverPort: String(setup.server.port),
    serverPassword: '',
    llmBaseUrl: setup.llm.baseUrl ?? '',
    llmModel: setup.llm.model ?? '',
    llmApiKey: '',
    trigger: setup.statuses.trigger,
    work: setup.statuses.work,
    review: setup.statuses.review,
    approved: setup.statuses.approved,
    maxParallelJobs: String(setup.maxParallelJobs ?? 1),
    jobTimeoutMinutes: String(setup.dev?.jobTimeoutMinutes ?? 120),
    maxDiffLines: String(setup.dev?.maxDiffLines ?? 2000),
    maxFiles: String(setup.dev?.maxFiles ?? 50),
    branchPrefix: setup.branchPrefix ?? 'ai/',
  };
}

function bodyOf(form: FormState): UpdateAgentSetupBody {
  return {
    serverHost: form.serverHost.trim(),
    serverPort: Number(form.serverPort),
    ...(form.serverPassword === '' ? {} : { serverPassword: form.serverPassword }),
    ...(form.llmBaseUrl.trim() === '' ? {} : { llmBaseUrl: form.llmBaseUrl.trim() }),
    ...(form.llmModel.trim() === '' ? {} : { llmModel: form.llmModel.trim() }),
    ...(form.llmApiKey === '' ? {} : { llmApiKey: form.llmApiKey }),
    statuses: {
      trigger: form.trigger.trim(),
      work: form.work.trim(),
      review: form.review.trim(),
      approved: form.approved.trim(),
    },
    maxParallelJobs: Number(form.maxParallelJobs),
    devJobTimeoutMinutes: Number(form.jobTimeoutMinutes),
    devMaxDiffLines: Number(form.maxDiffLines),
    devMaxFiles: Number(form.maxFiles),
    branchPrefix: form.branchPrefix.trim(),
  };
}

function JobProgress({ job, onCancel }: { readonly job: AgentInstallJob; readonly onCancel: () => void }) {
  return (
    <PageSection
      title={
        <>
          Установка{' '}
          <Badge tone={job.status === 'succeeded' ? 'success' : job.status === 'failed' ? 'danger' : 'progress'}>
            {job.status}
          </Badge>
        </>
      }
    >
      <ul className={styles['steps']}>
        {job.steps.map((step) => (
          <li key={step.key} className={styles['step']}>
            <span
              aria-hidden
              className={`${styles['dot']} ${
                step.status === 'running'
                  ? styles['dotRunning']
                  : step.status === 'ok'
                    ? styles['dotOk']
                    : step.status === 'failed'
                      ? styles['dotFailed']
                      : ''
              }`}
            />
            {step.label}
            <span className={styles['muted']}>{step.status}</span>
          </li>
        ))}
      </ul>
      {job.errorCode ? (
        <p className={styles['error']}>
          {job.errorCode}: {job.errorMessage}
        </p>
      ) : null}
      <pre className={styles['log']}>{job.logTail || 'Журнал пока пуст'}</pre>
      <div className={styles['actions']}>
        {(job.status === 'queued' || job.status === 'running') && (
          <Button variant="danger" size="sm" onClick={onCancel}>
            Отменить
          </Button>
        )}
        <a href={`/api/admin/agents/${job.agentId}/install/${job.id}/log`} download>
          <Button variant="ghost" size="sm" icon={<Download size={14} />}>
            Скачать лог
          </Button>
        </a>
      </div>
    </PageSection>
  );
}

/** `/agents/:id` — мастер настройки и установки (docs/16 §3.2). */
function formatBytes(value: number): string {
  return value < 1024 ? `${value} Б` : `${(value / 1024).toFixed(1)} КБ`;
}

export function AgentSetupPage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const fileInput = useRef<HTMLInputElement>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [uninstallName, setUninstallName] = useState('');
  const [pendingFingerprint, setPendingFingerprint] = useState<string | null>(null);
  const [deleteData, setDeleteData] = useState(false);
  const [isDeleteOpen, setDeleteOpen] = useState(false);

  const setupQuery = useQuery({
    queryKey: queryKeys.agents.setup(id),
    queryFn: ({ signal }) => getAgentSetup(id, signal),
    enabled: id.length > 0,
    refetchInterval: (query) => {
      const status = query.state.data?.install.status;
      return status === 'queued' || status === 'installing' ? 3000 : false;
    },
  });
  const installQuery = useQuery({
    queryKey: queryKeys.agents.install(id),
    queryFn: ({ signal }) => getAgentInstall(id, signal),
    enabled: id.length > 0 && setupQuery.data !== undefined,
    retry: false,
    refetchInterval: (query) => {
      const status = query.state.data?.job.status;
      return status === 'queued' || status === 'running' ? 2000 : false;
    },
  });
  const skillsQuery = useQuery({
    queryKey: queryKeys.agents.skills(id),
    queryFn: ({ signal }) => listAgentSkills(id, signal),
    enabled: id.length > 0,
  });

  useEffect(() => {
    if (setupQuery.data) setForm(formOf(setupQuery.data));
  }, [setupQuery.data]);

  const reportError = (cause: unknown): void => {
    const human = humanizeError(cause);
    toast.error(human.message, { code: human.code });
  };

  const saveMutation = useMutation({
    mutationFn: (body: UpdateAgentSetupBody) => updateAgentSetup(id, body),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.agents.setup(id), data);
      setForm(formOf(data));
      toast.success('Настройки сохранены');
    },
    onError: reportError,
  });

  const testServerMutation = useMutation({
    mutationFn: async () => {
      if (!form) throw new Error('Форма не загружена');
      await saveMutation.mutateAsync(bodyOf(form));
      return testAgentServer(id);
    },
    onSuccess: (result) => {
      toast.success(`Сервер доступен: ${result.os ?? 'Ubuntu'}, ${result.arch ?? ''}`);
      if (result.fingerprint !== null && result.fingerprint !== setupQuery.data?.server.fingerprint) {
        setPendingFingerprint(result.fingerprint);
      }
      void queryClient.invalidateQueries({ queryKey: queryKeys.agents.setup(id) });
    },
    onError: reportError,
  });

  const testLlmMutation = useMutation({
    mutationFn: async () => {
      if (!form) throw new Error('Форма не загружена');
      await saveMutation.mutateAsync(bodyOf(form));
      return testAgentLlm(id);
    },
    onSuccess: (result) => {
      toast.success(
        result.toolCalling
          ? `LLM отвечает за ${result.latencyMs ?? '—'} мс, tool-calling есть`
          : `LLM отвечает, но tool-calling не подтверждён`,
      );
      void queryClient.invalidateQueries({ queryKey: queryKeys.agents.setup(id) });
    },
    onError: reportError,
  });

  const installMutation = useMutation({
    mutationFn: async () => {
      if (!form) throw new Error('Форма не загружена');
      await saveMutation.mutateAsync(bodyOf(form));
      return startSharedAgentInstall(id);
    },
    onSuccess: () => {
      toast.success('Установка запущена');
      void queryClient.invalidateQueries({ queryKey: queryKeys.agents.setup(id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.agents.install(id) });
    },
    onError: reportError,
  });

  const updateMutation = useMutation({
    mutationFn: () => updateAgent(id),
    onSuccess: () => {
      toast.success('Обновление запущено');
      void queryClient.invalidateQueries({ queryKey: queryKeys.agents.install(id) });
    },
    onError: reportError,
  });

  const applyMutation = useMutation({
    mutationFn: () => applyAgent(id),
    onSuccess: () => {
      toast.success('Настройки применяются');
      void queryClient.invalidateQueries({ queryKey: queryKeys.agents.install(id) });
    },
    onError: reportError,
  });

  const uninstallMutation = useMutation({
    mutationFn: () => uninstallAgent(id, { confirmName: uninstallName, deleteData }),
    onSuccess: () => {
      toast.success('Удаление запущено');
      setUninstallName('');
      void queryClient.invalidateQueries({ queryKey: queryKeys.agents.setup(id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.agents.install(id) });
    },
    onError: reportError,
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteAgent(id),
    onSuccess: () => {
      setDeleteOpen(false);
      toast.success('Агент удалён');
      void queryClient.invalidateQueries({ queryKey: queryKeys.agents.list });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      navigate('/agents');
    },
    onError: reportError,
  });

  const cancelMutation = useMutation({
    mutationFn: (jobId: string) => cancelAgentInstall(id, jobId),
    onSuccess: () => {
      toast.success('Отмена запрошена');
      void queryClient.invalidateQueries({ queryKey: queryKeys.agents.install(id) });
    },
    onError: reportError,
  });

  const uploadSkillsMutation = useMutation({
    mutationFn: async (files: File[]) => {
      for (const file of files) {
        if (!file.name.endsWith('.md')) {
          throw new Error(`Файл «${file.name}»: нужен markdown с расширением .md`);
        }
        if (file.size > AGENT_SKILL_MAX_BYTES) {
          throw new Error(`Файл «${file.name}» больше 64 КБ`);
        }
        await uploadAgentSkill(id, file.name, await file.text());
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.agents.skills(id) });
      toast.success(
        setupQuery.data?.install.status === 'installed'
          ? 'Навыки сохранены. Примените настройки на сервере.'
          : 'Навыки сохранены',
      );
    },
    onError: reportError,
  });

  const deleteSkillMutation = useMutation({
    mutationFn: (filename: string) => deleteAgentSkill(id, filename),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.agents.skills(id) });
      toast.success(
        setupQuery.data?.install.status === 'installed'
          ? 'Навык удалён. Примените настройки на сервере.'
          : 'Навык удалён',
      );
    },
    onError: reportError,
  });

  const confirmFingerprint = useMutation({
    mutationFn: (fingerprint: string) => updateAgentSetup(id, { serverFingerprint: fingerprint }),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.agents.setup(id), data);
      setPendingFingerprint(null);
      toast.success('Отпечаток подтверждён');
    },
    onError: reportError,
  });

  if (setupQuery.isPending) {
    return (
      <Page width="wide">
        <Skeleton variant="row" count={8} />
      </Page>
    );
  }

  if (setupQuery.isError) {
    return (
      <Page width="wide">
        <EmptyState icon={<Server size={20} />} title="Нет доступа к агенту" description="Раздел доступен Admin и Project Admin проекта." />
        <Link to="/agents">
          <Button variant="ghost" icon={<ArrowLeft size={14} />}>
            К списку
          </Button>
        </Link>
      </Page>
    );
  }

  if (form === null) {
    return (
      <Page width="wide">
        <Skeleton variant="row" count={8} />
      </Page>
    );
  }

  const setup = setupQuery.data;
  const job = installQuery.data?.job ?? null;
  const skills = skillsQuery.data?.items ?? [];
  const isInstalled = setup.install.status === 'installed';
  // Из Tasks удаляет только глобальный админ и только когда на сервере ничего не работает.
  const canDeleteAgent =
    user?.role === 'admin' &&
    (setup.install.status === 'not_installed' || setup.install.status === 'failed');
  const isReady =
    setup.server.lastTest !== null &&
    setup.server.fingerprint !== null &&
    setup.server.lastTest.fingerprint === setup.server.fingerprint &&
    setup.llm.lastTest !== null;
  const setField = (field: keyof FormState, value: string): void =>
    setForm((current) => (current === null ? current : { ...current, [field]: value }));

  return (
    <Page width="wide">
      <PageHeader
        backLink={
          <Link to="/agents" className="u-muted">
            ← AI-агенты
          </Link>
        }
        title={setup.agent.displayName}
        description={`${setup.agent.agentRole === 'developer' ? 'AI-разработчик' : 'AI-аналитик'} · @${setup.agent.login}`}
        actions={
          <>
            <Button
              variant="secondary"
              loading={saveMutation.isPending}
              onClick={() => saveMutation.mutate(bodyOf(form))}
            >
              Сохранить
            </Button>
            <Badge tone={STATUS_TONE[setup.install.status] ?? 'neutral'}>
              {STATUS_LABEL[setup.install.status] ?? setup.install.status}
            </Badge>
          </>
        }
      />

      <p className={styles['warning']}>
        {setup.agent.agentRole === 'developer'
          ? 'Это AI-разработчик: он обслуживает несколько проектов. Проекты добавляются на вкладке «AI-агенты» проекта. Установка разворачивает runner, PostgreSQL и LLM-proxy на одном VPS.'
          : 'Это AI-аналитик: он обслуживает несколько проектов. Проекты, их статусы и репозитории добавляются на вкладке «AI-агенты» проекта; повторная установка для нового проекта не нужна.'}
      </p>

      <div className={styles['grid']}>
        <PageSection title="1. Сервер">
          <div className={styles['form']}>
            <Input
              label="Сервер"
              value={form.serverHost}
              placeholder="root@203.0.113.10"
              onChange={(event) => setField('serverHost', event.target.value)}
            />
            <Input
              label="SSH-порт"
              type="number"
              min={1}
              max={65535}
              value={form.serverPort}
              onChange={(event) => setField('serverPort', event.target.value)}
            />
            <Input
              name="agent-server-password"
              label="Root-пароль"
              type="password"
              autoComplete="new-password"
              spellCheck={false}
              value={form.serverPassword}
              placeholder={setup.server.hasPassword ? 'Пароль задан — оставьте пустым' : 'Введите пароль'}
              onChange={(event) => setField('serverPassword', event.target.value)}
            />
            {setup.server.lastTest ? (
              <p className={styles['muted']}>
                {setup.server.lastTest.os} · {setup.server.lastTest.arch} · {setup.server.lastTest.cpu} vCPU ·{' '}
                {setup.server.fingerprint ?? 'отпечаток не подтверждён'}
              </p>
            ) : null}
            {pendingFingerprint ? (
              <div className={styles['warning']}>
                Отпечаток сервера: <code>{pendingFingerprint}</code>. Сверьте его с сервером и подтвердите.
                <div className={styles['actions']}>
                  <Button
                    variant="secondary"
                    size="sm"
                    loading={confirmFingerprint.isPending}
                    onClick={() => confirmFingerprint.mutate(pendingFingerprint)}
                  >
                    Отпечаток совпадает
                  </Button>
                </div>
              </div>
            ) : null}
            <div className={styles['actions']}>
              <Button
                variant="secondary"
                loading={testServerMutation.isPending}
                icon={<Server size={14} />}
                onClick={() => testServerMutation.mutate()}
              >
                Проверить сервер
              </Button>
            </div>
          </div>
        </PageSection>

        <PageSection title="2. LLM">
          <div className={styles['form']}>
            <Input
              label="Base URL"
              value={form.llmBaseUrl}
              placeholder="https://api.openai.com/v1"
              onChange={(event) => setField('llmBaseUrl', event.target.value)}
            />
            <Input
              label="Модель"
              value={form.llmModel}
              placeholder="gpt-4.1"
              onChange={(event) => setField('llmModel', event.target.value)}
            />
            <Input
              name="agent-llm-api-key"
              label="API-ключ"
              type="password"
              autoComplete="new-password"
              spellCheck={false}
              value={form.llmApiKey}
              placeholder={setup.llm.hasApiKey ? 'Ключ задан — оставьте пустым' : 'Введите ключ'}
              onChange={(event) => setField('llmApiKey', event.target.value)}
            />
            {setup.llm.lastTest ? (
              <p className={styles['muted']}>
                {setup.llm.lastTest.model} · {setup.llm.lastTest.latencyMs ?? '—'} мс · tool-calling:{' '}
                {setup.llm.lastTest.toolCalling ? 'да' : 'нет'}
              </p>
            ) : null}
            <div className={styles['actions']}>
              <Button
                variant="secondary"
                loading={testLlmMutation.isPending}
                icon={<Sparkles size={14} />}
                onClick={() => testLlmMutation.mutate()}
              >
                Проверить LLM
              </Button>
            </div>
          </div>
        </PageSection>

        <PageSection title={setup.agent.agentRole === 'developer' ? '3. Разработка' : '3. Очередь'}>
            <div className={styles['grid']}>
              <Input
                label="Параллельных задач"
                type="number"
                min={1}
                max={10}
                value={form.maxParallelJobs}
                hint="Общая очередь агента; один проект одновременно занимает не больше одного слота"
                onChange={(event) => setField('maxParallelJobs', event.target.value)}
              />
              {setup.agent.agentRole === 'developer' ? (
                <>
                  <Input
                    label="Таймаут job, мин"
                    type="number"
                    value={form.jobTimeoutMinutes}
                    onChange={(event) => setField('jobTimeoutMinutes', event.target.value)}
                  />
                  <Input
                    label="Лимит строк диффа"
                    type="number"
                    value={form.maxDiffLines}
                    onChange={(event) => setField('maxDiffLines', event.target.value)}
                  />
                  <Input
                    label="Лимит файлов"
                    type="number"
                    value={form.maxFiles}
                    onChange={(event) => setField('maxFiles', event.target.value)}
                  />
                  <Input
                    label="Префикс ветки"
                    value={form.branchPrefix}
                    onChange={(event) => setField('branchPrefix', event.target.value)}
                  />
                </>
              ) : (
                <p className={styles['muted']}>
                  Лимиты LLM и навыки общие для всех проектов; проектных лимитов в
                  этой версии нет.
                </p>
              )}
            </div>
          </PageSection>
        <PageSection className={styles['wide']} title="4. Навыки">
          <p className={styles['muted']}>
            Свои SKILL.md: правила и формат работы, которые агент добавляет к системному
            промпту. До 20 файлов, каждый до 64 КБ; файл с тем же именем заменяется.
          </p>
          <div className={styles['actions']}>
            <input
              ref={fileInput}
              type="file"
              accept=".md,text/markdown"
              multiple
              hidden
              aria-label="Файлы навыков"
              onChange={(event) => {
                const files = Array.from(event.currentTarget.files ?? []);
                event.currentTarget.value = '';
                if (files.length > 0) uploadSkillsMutation.mutate(files);
              }}
            />
            <Button
              variant="secondary"
              icon={<Upload size={14} />}
              loading={uploadSkillsMutation.isPending}
              onClick={() => fileInput.current?.click()}
            >
              Загрузить .md
            </Button>
          </div>
          {isInstalled ? (
            <p className={styles['warning']}>
              Изменения попадут к агенту после кнопки «Применить настройки».
            </p>
          ) : null}
          {skillsQuery.isPending ? (
            <Skeleton variant="row" count={2} />
          ) : skillsQuery.isError ? (
            <p className={styles['error']}>Не удалось загрузить навыки</p>
          ) : skills.length === 0 ? (
            <p className={styles['muted']}>
              Навыков пока нет. Загрузите SKILL.md с правилами для этого агента.
            </p>
          ) : (
            <ul className={styles['skills']}>
              {skills.map((skill) => (
                <li key={skill.id} className={styles['skill']}>
                  <span className={styles['name']}>{skill.filename}</span>
                  <span className={styles['muted']}>{formatBytes(skill.sizeBytes)}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    icon={<Trash2 size={14} />}
                    aria-label={`Удалить ${skill.filename}`}
                    loading={
                      deleteSkillMutation.isPending &&
                      deleteSkillMutation.variables === skill.filename
                    }
                    onClick={() => deleteSkillMutation.mutate(skill.filename)}
                  >
                    Удалить
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </PageSection>

        <PageSection title="5. Установка">
          <p className={styles['muted']}>
            Docker, PostgreSQL 16 и агент. Наружу откроется только SSH; порты 80/443 не используются.
          </p>
          <div className={styles['actions']}>
            <Button
              variant="primary"
              loading={installMutation.isPending}
              icon={<Play size={14} />}
              disabled={!isInstalled && !isReady}

              onClick={() => installMutation.mutate()}
            >
              {isInstalled ? 'Переустановить' : 'Установить'}
            </Button>
            {isInstalled ? (
              <>
                <Button variant="secondary" loading={applyMutation.isPending} onClick={() => applyMutation.mutate()}>
                  Применить настройки
                </Button>
                <Button variant="secondary" loading={updateMutation.isPending} onClick={() => updateMutation.mutate()}>
                  Обновить
                </Button>
              </>
            ) : null}
          </div>
          {setup.install.installedAt ? (
            <p className={styles['muted']}>
              Версия {setup.install.version ?? '—'} · установлен {setup.install.installedAt}
            </p>
          ) : null}
        </PageSection>
      </div>

      <PageSection>
        <details>
          <summary>Дополнительно: имена статусов</summary>
          <div className={styles['grid']}>
            <Input label="К выполнению" value={form.trigger} onChange={(event) => setField('trigger', event.target.value)} />
            <Input label="В работе" value={form.work} onChange={(event) => setField('work', event.target.value)} />
            <Input label="На согласовании" value={form.review} onChange={(event) => setField('review', event.target.value)} />
            <Input label="Утверждено" value={form.approved} onChange={(event) => setField('approved', event.target.value)} />
          </div>
        </details>
      </PageSection>

      {job ? <JobProgress job={job} onCancel={() => cancelMutation.mutate(job.id)} /> : null}

      {isInstalled ? (
        <PageSection title="Удаление">
          <p className={styles['warning']}>
            Контейнеры будут остановлены, API-токен отозван. Данные удаляются только по отдельному подтверждению.
          </p>
          <div className={styles['form']}>
            <Input
              label="Введите имя агента для подтверждения"
              value={uninstallName}
              onChange={(event) => setUninstallName(event.target.value)}
            />
            <label className={styles['row']}>
              <input type="checkbox" checked={deleteData} onChange={(event) => setDeleteData(event.target.checked)} />
              Удалить данные PostgreSQL и git-клон
            </label>
            <Button
              variant="danger"
              icon={<Trash2 size={14} />}
              loading={uninstallMutation.isPending}
              disabled={uninstallName !== setup.agent.displayName}
              onClick={() => uninstallMutation.mutate()}
            >
              Удалить агента
            </Button>
          </div>
          <p className={styles['muted']}>
            После удаления с сервера агента можно удалить из Tasks.
          </p>
        </PageSection>
      ) : canDeleteAgent ? (
        <PageSection title="Удаление">
          <p className={styles['warning']}>
            Агент будет удалён из Tasks вместе с настройками и навыками. Задачи, комментарии и журнал
            действий сохранятся; на сервере ничего не изменится.
          </p>
          <div className={styles['actions']}>
            <Button variant="danger" icon={<Trash2 size={14} />} onClick={() => setDeleteOpen(true)}>
              Удалить агента
            </Button>
          </div>
        </PageSection>
      ) : null}

      <ConfirmDialog
        open={isDeleteOpen}
        title="Удалить агента из Tasks?"
        description={`Агент «${setup.agent.displayName}» исчезнет из списка. Настройки и навыки удалятся безвозвратно; задачи, комментарии и аудит сохранятся.`}
        confirmLabel="Удалить агента"
        confirmPhrase={setup.agent.displayName}
        isPending={deleteMutation.isPending}
        onClose={() => setDeleteOpen(false)}
        onConfirm={() => deleteMutation.mutate()}
      />
    </Page>
  );
}
